/**
 * hostTool — maps a tool subdomain (e.g. `logscaling.beetlesense.ai`) to the
 * internal route that should render there.
 *
 * Why client-side: a Vercel *rewrite* of `/` → `/tools/...` serves the SPA
 * shell but leaves the browser path at `/`, so React Router would still render
 * the landing page. The app therefore detects its own hostname on boot and
 * routes to the tool itself. Pure + testable; the registries are the source of
 * truth so a new addressed blade needs no change here.
 */
import { getModuleByDomain } from '@/services/modules/registry'
import { getBladeByDomain } from '@/services/blades/bladeRegistry'
import { isMagazineHost } from '@/services/magazine/site'

/**
 * The internal route for a given hostname, or null when the host isn't a tool
 * subdomain (apex `beetlesense.ai`, `app.`, localhost, previews → null = normal
 * home behaviour). Case- and `www.`-insensitive (the registry lookups handle that).
 */
export function toolRouteForHost(hostname: string | undefined | null): string | null {
  if (!hostname) return null

  // The magazine's own standalone domain → the magazine index.
  if (isMagazineHost(hostname)) return '/magazine'

  // Compute-blade subdomains → their tool route (/tools/...).
  const blade = getBladeByDomain(hostname)
  if (blade) return blade.route

  // Scan/instrument module subdomains → their public door.
  const mod = getModuleByDomain(hostname)
  if (mod) {
    // relascope/acoustic have dedicated door pages; the photo modules use the
    // templated /d/:id marketing door.
    if (mod.id === 'relascope' || mod.id === 'acoustic') return mod.doorRoute
    return `/d/${mod.id}`
  }

  return null
}

/** Convenience: the route for the current browser host, or null (SSR-safe). */
export function toolRouteForCurrentHost(): string | null {
  if (typeof window === 'undefined') return null
  return toolRouteForHost(window.location.hostname)
}
